import { __decorate, __metadata } from "tslib";
// eslint-disable-next-line max-classes-per-file
import { Serialize } from '@ftr/serialization';
import { ChronoUnit, LocalDateTime } from '@js-joda/core';
import { LocalDateRange } from './LocalDateRange';
import { LocalTimeRange } from './LocalTimeRange';
import { TimeRange } from './TimeRange';
import { Timecode } from './Timecode';
/**
 * Represents a LocalDateTime time range. Contains two LocalDateTime instances, one that
 * specifies the start of the range and another that specifies the end of the range.
 */
export class LocalDateTimeRange extends TimeRange {
    static parse(start, end) {
        return new LocalDateTimeRange(LocalDateTime.parse(start), LocalDateTime.parse(end));
    }
    /**
     * Returns the range with the earliest start date and time from the given array.
     * @throws Error if array is empty
     */
    static getEarliest(ranges) {
        if (!ranges.length) {
            throw new Error('Empty array');
        }
        return [...ranges].sort((a, b) => a.start.compareTo(b.start)).shift();
    }
    /**
     * Returns the range with the latest end date and time from the given array.
     * @throws Error if array is empty
     */
    static getLatest(ranges) {
        if (!ranges.length) {
            throw new Error('Empty array');
        }
        return [...ranges].sort((a, b) => a.end.compareTo(b.end)).pop();
    }
    /**
     * Creates a range for the provided `date` within `start` to `end` times.
     */
    static of(date, start, end) {
        return new LocalDateTimeRange(LocalDateTime.of(date, start), LocalDateTime.of(date, end));
    }
    static fromTimecode(timecode) {
        return new LocalDateTimeRange(timecode.start, timecode.end);
    }
    static fromTimeFrame(timeFrame) {
        return new LocalDateTimeRange(timeFrame.startTime, timeFrame.endTime);
    }
    static fromLocalTimeRange(localTimeRange, date) {
        return new LocalDateTimeRange(date.atTime(localTimeRange.start), date.atTime(localTimeRange.end));
    }
    /**
     * Adds time ranges durations together to return the total milliseconds
     * @param ranges An array of time ranges
     */
    static addTimeRanges(ranges) {
        return ranges.reduce((duration, timeRange) => {
            const { end, start } = timeRange;
            if (end.isBefore(start)) {
                throw new InvalidTimeRange(timeRange);
            }
            return duration + start.until(end, ChronoUnit.MILLIS);
        }, 0);
    }
    toTimecode() {
        return new Timecode(this.start, this.duration.toMillis());
    }
    toLocalTimeRange() {
        return new LocalTimeRange(this.start.toLocalTime(), this.end.toLocalTime());
    }
    toLocalDateRange() {
        return new LocalDateRange(this.start.toLocalDate(), this.end.toLocalDate());
    }
    toTimeFrame() {
        return { startTime: this.start, endTime: this.end };
    }
    toTimeframe() {
        return { start: this.start.toLocalTime().toSecondOfDay(), end: this.end.toLocalTime().toSecondOfDay() };
    }
}
__decorate([
    Serialize(),
    __metadata("design:type", LocalDateTime)
], LocalDateTimeRange.prototype, "start", void 0);
__decorate([
    Serialize(),
    __metadata("design:type", LocalDateTime)
], LocalDateTimeRange.prototype, "end", void 0);
export class InvalidTimeRange extends Error {
    constructor({ start, end }) {
        super(`The duration between ${start.toString()} and ${end.toString()} is invalid.`);
        Object.setPrototypeOf(this, new.target.prototype);
    }
}
